import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  AccountTreeRounded,
  CloseRounded,
  DashboardRounded,
  ExpandLess,
  ExpandMore,
  FactoryRounded,
  Inventory2Rounded,
  MenuBookRounded,
  MenuRounded,
  PaymentsRounded,
  PrecisionManufacturingRounded,
  SettingsRounded,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import IndustrialCostGuideView from '../views/IndustrialCostGuideView';
import IndustrialCostSummaryView from '../views/IndustrialCostSummaryView';
import IndustrialCostStagesView from '../views/IndustrialCostStagesView';
import IndustrialCostProductsView from '../views/IndustrialCostProductsView';
import IndustrialCostMovementsView from '../views/IndustrialCostMovementsView';
import IndustrialCostClassificationView from '../views/IndustrialCostClassificationView';
import IndustrialCostDirectCostsView from '../views/IndustrialCostDirectCostsView';
import IndustrialCostDirectInputsView from '../views/IndustrialCostDirectInputsView';
import IndustrialCostLaborView from '../views/IndustrialCostLaborView';
import IndustrialCostTransformationsView from '../views/IndustrialCostTransformationsView';
import IndustrialCostTechnicalConsumptionsView from '../views/IndustrialCostTechnicalConsumptionsView';
import IndustrialCostDirectMaterialsView from '../views/IndustrialCostDirectMaterialsView';
import IndustrialCostDirectMaterialValuationsView from '../views/IndustrialCostDirectMaterialValuationsView';
import IndustrialCostPrimaryAllocationsView from '../views/IndustrialCostPrimaryAllocationsView';
import IndustrialCostSecondaryAllocationsView from '../views/IndustrialCostSecondaryAllocationsView';
import IndustrialCostProductionView from '../views/IndustrialCostProductionView';
import IndustrialCostFinalAllocationRulesView from '../views/IndustrialCostFinalAllocationRulesView';
import IndustrialCostResultsView from '../views/IndustrialCostResultsView';
import IndustrialCostInventoryRegisterView from '../views/IndustrialCostInventoryRegisterView';
import IndustrialCostRecountSheetView from '../views/IndustrialCostRecountSheetView';
import IndustrialCostSupplierMatchesView from '../views/IndustrialCostSupplierMatchesView';
import IndustrialCostMaterialControlView from '../views/IndustrialCostMaterialControlView';
import IndustrialCostPlantDashboardView from '../views/IndustrialCostPlantDashboardView';
import IndustrialCostWarehouseLayoutView from '../views/IndustrialCostWarehouseLayoutView';
import IndustrialCostProductionReportsOperatorView from '../views/IndustrialCostProductionReportsOperatorView';
import IndustrialCostProductionReportsControlView from '../views/IndustrialCostProductionReportsControlView';
import IndustrialCostProductionReportsConfigView from '../views/IndustrialCostProductionReportsConfigView';
import IndustrialCostProductionPlanningView from '../views/IndustrialCostProductionPlanningView';
import IndustrialCostDataSourcesView from '../views/IndustrialCostDataSourcesView';
import IndustrialCostPlantUsersView from '../views/IndustrialCostPlantUsersView';
import IndustrialWorkflowsCasesView from '../../industrialWorkflows/views/IndustrialWorkflowsCasesView';
import IndustrialWorkflowsConfigView from '../../industrialWorkflows/views/IndustrialWorkflowsConfigView';
import { getIndustrialWorkflowsContext } from '../../industrialWorkflows/services/industrialWorkflowsService';
import { getPlatformAdminContext } from '../../platformAdmin/services/platformAdminService';
import { getIndustrialCostContext } from '../services/industrialCostService';
import { useAuth } from '../../../context/AuthContext';
import type { IndustrialCostViewKey } from '../types/industrialCost';

const RevealSection = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay, ease: 'easeOut' }}
    style={{ height: '100%' }}
  >
    {children}
  </motion.div>
);

type ProductionPlanningViewKey = 'production-planning';

type IndustrialWorkflowsViewKey =
  | 'workflows-cases'
  | 'workflows-config';

type IndustrialCostPageViewKey =
  | IndustrialCostViewKey
  | ProductionPlanningViewKey
  | IndustrialWorkflowsViewKey;

type NavGroupKey =
  | 'workflows'
  | 'indirect-costs'
  | 'direct-production'
  | 'plant'
  | 'inventory'
  | 'setup';

const WORKFLOWS_VIEWS: IndustrialCostPageViewKey[] = [
  'workflows-cases',
  'workflows-config',
];

const INDIRECT_COST_VIEWS: IndustrialCostPageViewKey[] = [
  'movements',
  'classification',
  'primary-allocations',
  'secondary-allocations',
  'final-allocation-rules',
];

const DIRECT_PRODUCTION_VIEWS: IndustrialCostPageViewKey[] = [
  'production',
  'direct-inputs',
  'labor',
  'transformations',
  'technical-consumptions',
  'direct-material-valuations',
  'applied-direct-costs',
  'results',
];

const PLANT_VIEWS: IndustrialCostPageViewKey[] = [
  'plant-dashboard',
  'production-reports-operator',
  'production-reports-control',
  'production-planning',
  'production-reports-config',
];

const INVENTORY_VIEWS: IndustrialCostPageViewKey[] = [
  'inventory-register',
  'material-control',
  'warehouse-layout',
  'supplier-matches',
  'recount-sheet',
];

const SETUP_VIEWS: IndustrialCostPageViewKey[] = [
  'products',
  'stages',
  'direct-materials',
  'direct-cost-categories',
  'plant-users',
  'import-center',
];

const COSTS_VIEWS: IndustrialCostPageViewKey[] = [
  'movements',
  'classification',
  'primary-allocations',
  'secondary-allocations',
  'final-allocation-rules',
  'production',
  'direct-inputs',
  'labor',
  'transformations',
  'technical-consumptions',
  'direct-material-valuations',
  'applied-direct-costs',
  'products',
  'stages',
  'direct-materials',
  'direct-cost-categories',
  'import-center',
];

const ALL_INDUSTRIAL_COST_PAGE_VIEWS: IndustrialCostPageViewKey[] = [
  'summary',
  'guide',
  ...WORKFLOWS_VIEWS,
  ...INDIRECT_COST_VIEWS,
  ...DIRECT_PRODUCTION_VIEWS,
  ...PLANT_VIEWS,
  ...INVENTORY_VIEWS,
  ...SETUP_VIEWS,
];

function getIndustrialCostPageViewFromUrl(): IndustrialCostPageViewKey | null {
  const requestedView = new URLSearchParams(
    window.location.search,
  ).get('view');

  if (
    !requestedView ||
    !ALL_INDUSTRIAL_COST_PAGE_VIEWS.includes(
      requestedView as IndustrialCostPageViewKey,
    )
  ) {
    return null;
  }

  return requestedView as IndustrialCostPageViewKey;
}

function replaceIndustrialCostPageViewInUrl(
  view: IndustrialCostPageViewKey,
) {
  const url = new URL(window.location.href);

  if (url.searchParams.get('view') === view) {
    return;
  }

  url.searchParams.set('view', view);
  // IND-033: la vista «Desperdicio» es del Panel: al irse no se queda abierta.
  url.searchParams.delete('panel');

  window.history.replaceState(
    null,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function hasIndustrialPermission(
  permissions: string[] | undefined,
  permission: string,
) {
  return Boolean(permissions?.includes(permission));
}

function hasAnyIndustrialPermission(
  permissions: string[] | undefined,
  requiredPermissions: string[],
) {
  return requiredPermissions.some((permission) => permissions?.includes(permission));
}

function isViewInGroup(view: IndustrialCostPageViewKey, group: NavGroupKey) {
  if (group === 'workflows') return WORKFLOWS_VIEWS.includes(view);
  if (group === 'indirect-costs') return INDIRECT_COST_VIEWS.includes(view);
  if (group === 'direct-production') return DIRECT_PRODUCTION_VIEWS.includes(view);
  if (group === 'plant') return PLANT_VIEWS.includes(view);
  if (group === 'inventory') return INVENTORY_VIEWS.includes(view);
  return SETUP_VIEWS.includes(view);
}

type NavItemProps = {
  label: string;
  selected: boolean;
  nested?: boolean;
  onClick: () => void;
};

function NavItem({ label, selected, nested = false, onClick }: NavItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        minHeight: nested ? 40 : 46,
        borderRadius: 2.5,
        px: nested ? 2.25 : 2,
        pl: nested ? 4 : 2,
        mb: 0.5,
        bgcolor: selected ? alpha('#38bdf8', 0.12) : 'transparent',
        border: `1px solid ${selected ? alpha('#38bdf8', 0.18) : 'transparent'}`,
        color: selected ? '#f8fafc' : '#94a3b8',
        transition: 'all 0.18s ease',
        '&:hover': {
          bgcolor: selected ? alpha('#38bdf8', 0.14) : alpha('#ffffff', 0.03),
          color: '#e2e8f0',
        },
      }}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: nested ? '0.93rem' : '0.98rem',
          fontWeight: selected ? 800 : 700,
          lineHeight: 1.2,
        }}
      />
    </ListItemButton>
  );
}

type NavGroupProps = {
  title: string;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

function NavGroup({ title, expanded, active, onToggle, children }: NavGroupProps) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <ListItemButton
        onClick={onToggle}
        sx={{
          minHeight: 46,
          borderRadius: 2.5,
          px: 2,
          mb: 0.5,
          bgcolor: active ? alpha('#ffffff', 0.03) : 'transparent',
          color: active ? '#f8fafc' : '#cbd5e1',
          transition: 'all 0.18s ease',
          '&:hover': {
            bgcolor: alpha('#ffffff', 0.03),
          },
        }}
      >
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            fontSize: '0.98rem',
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        />
        {expanded ? (
          <ExpandLess sx={{ color: active ? '#e2e8f0' : '#64748b' }} />
        ) : (
          <ExpandMore sx={{ color: active ? '#e2e8f0' : '#64748b' }} />
        )}
      </ListItemButton>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 0.25 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

type DesktopNavRailButtonProps = {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick?: () => void;
};

function DesktopNavRailButton({
  label,
  icon,
  selected,
  onClick,
}: DesktopNavRailButtonProps) {
  return (
    <IconButton
      title={label}
      aria-label={label}
      onClick={onClick}
      sx={{
        width: 46,
        height: 46,
        mb: 0.5,
        p: 0,
        borderRadius: 2.5,
        color: selected ? '#f8fafc' : '#94a3b8',
        bgcolor: selected ? alpha('#38bdf8', 0.16) : 'transparent',
        border: `1px solid ${selected ? alpha('#38bdf8', 0.24) : 'transparent'}`,
        transition: 'all 0.18s ease',
        '& .MuiSvgIcon-root': {
          fontSize: 22,
        },
        '&:hover': {
          color: '#e2e8f0',
          bgcolor: selected ? alpha('#38bdf8', 0.18) : alpha('#ffffff', 0.05),
          borderColor: alpha('#38bdf8', 0.2),
        },
      }}
    >
      {icon}
    </IconButton>
  );
}

function DesktopNavRailGroupSpacer({
  count,
  visible,
}: {
  count: number;
  visible: boolean;
}) {
  if (!visible || count <= 0) {
    return null;
  }

  return (
    <Box
      sx={{
        height: count * 44,
        flexShrink: 0,
      }}
    />
  );
}

export default function IndustrialCostPage() {
  const { signOut } = useAuth();

  const shouldUseCompactSidebar = useMediaQuery(
    '(max-width:1199.95px), (max-width:1535.95px) and (any-pointer:coarse)',
  );

  const industrialContextQuery = useQuery({
    queryKey: ['industrial-cost-context'],
    queryFn: getIndustrialCostContext,
    retry: false,
  });

  const permissions = industrialContextQuery.data?.permissions ?? [];

  const industrialWorkflowsContextQuery = useQuery({
    queryKey: ['industrial-workflows-context'],
    queryFn: getIndustrialWorkflowsContext,
    retry: false,
  });

  const platformAdminContextQuery = useQuery({
    queryKey: ['platform-admin-context'],
    queryFn: getPlatformAdminContext,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const canAccessPlatformAdmin =
    platformAdminContextQuery.data?.is_platform_admin === true;

  const workflowsPermissions =
    industrialWorkflowsContextQuery.data?.permissions ?? [];

  const canViewWorkflowsCases =
    industrialWorkflowsContextQuery.data?.has_access === true &&
    hasAnyIndustrialPermission(workflowsPermissions, [
      'workflows:cases:view',
      'workflows:cases:manage',
    ]);

  const canManageWorkflowsConfig =
    industrialWorkflowsContextQuery.data?.has_access === true &&
    hasAnyIndustrialPermission(workflowsPermissions, [
      'workflows:processes:view',
      'workflows:processes:manage',
      'workflows:config:manage',
    ]);

  const canAccessWorkflows =
    canViewWorkflowsCases ||
    canManageWorkflowsConfig;

  const controlViewPermissions = useMemo(
    () =>
      permissions.filter(
        (
          permission,
        ): permission is
          | 'factory_control:view'
          | 'production_history:view'
          | 'production_performance:view'
          | 'production_planning:manage' =>
          permission === 'factory_control:view' ||
          permission === 'production_history:view' ||
          permission === 'production_performance:view' ||
          permission === 'production_planning:manage',

      ),
    [permissions],
  );

  const canViewFactoryControl = hasAnyIndustrialPermission(permissions, [
    'factory_control:view',
    'production_history:view',
    'production_performance:view',
  ]);

  const activeUserName =
    industrialContextQuery.data?.full_name ||
    industrialContextQuery.data?.email ||
    'Usuario';

  const activeUserSubtitle =
    industrialContextQuery.data?.email ||
    'Sesión activa';

  const canManagePlantConfig = hasIndustrialPermission(permissions, 'plant_config:manage');
  const canManagePlantUsers = hasIndustrialPermission(permissions, 'plant_users:manage');
  const canManageProductionPlanning = hasIndustrialPermission(
    permissions,
    'production_planning:manage',
  );
  const canManageInventory = hasIndustrialPermission(permissions, 'inventory:manage');
  const canViewCosts = hasIndustrialPermission(permissions, 'costs:view');
  // IND-007: Gestión de datos se abre con imports:manage o con costs:view.
  const canManageImports = hasIndustrialPermission(permissions, 'imports:manage');
  const canViewResults = hasIndustrialPermission(permissions, 'results:view');

  const canUseProductionReportsOperator = hasAnyIndustrialPermission(permissions, [
    'production_reports:view',
    'production_reports:operate',
    'production_reports:validate',
  ]);

  const canViewPlant =
    canManageImports ||
    canUseProductionReportsOperator ||
    canViewFactoryControl ||
    canManageProductionPlanning ||
    canManagePlantConfig ||
    canManageInventory;

  const defaultView: IndustrialCostPageViewKey = canViewCosts
    ? 'summary'
    : canUseProductionReportsOperator
      ? 'production-reports-operator'
      : canViewFactoryControl
        ? 'production-reports-control'
        : canManageProductionPlanning
          ? 'production-planning'
          : canManagePlantConfig
            ? 'production-reports-config'
            : canManageInventory
              ? 'inventory-register'
              : canManageWorkflowsConfig
                ? 'workflows-config'
                : canViewResults
                  ? 'results'
                  : 'summary';

  const accessibleViews = useMemo(() => {
    const nextViews = new Set<IndustrialCostPageViewKey>([
      'summary',
      'guide',
    ]);

    if (canViewCosts) {
      COSTS_VIEWS.forEach((allowedView) =>
        nextViews.add(allowedView),
      );
    }

    if (canViewResults) {
      nextViews.add('results');
    }

    if (canManageImports) {
      nextViews.add('import-center');
      // IND-039
      nextViews.add('supplier-matches');
    }

    if (canManageInventory) {
      INVENTORY_VIEWS.filter((allowedView) => allowedView !== 'supplier-matches').forEach((allowedView) =>
        nextViews.add(allowedView),
      );

      nextViews.add('plant-dashboard');
    }

    if (canUseProductionReportsOperator) {
      nextViews.add('production-reports-operator');
    }

    if (canViewFactoryControl) {
      nextViews.add('production-reports-control');
    }

    if (canManageProductionPlanning) {
      nextViews.add('production-planning');
    }

    if (canManagePlantConfig) {
      nextViews.add('production-reports-config');
    }

    if (canManagePlantUsers) {
      nextViews.add('plant-users');
    }

    if (canViewWorkflowsCases) {
      nextViews.add('workflows-cases');
    }

    if (canManageWorkflowsConfig) {
      nextViews.add('workflows-config');
    }

    return nextViews;
  }, [
    canManageImports,
    canManageInventory,
    canManagePlantConfig,
    canManagePlantUsers,
    canManageProductionPlanning,
    canManageWorkflowsConfig,
    canUseProductionReportsOperator,
    canViewCosts,
    canViewFactoryControl,
    canViewResults,
    canViewWorkflowsCases,
  ]);

  const [view, setView] = useState<IndustrialCostPageViewKey>(
    () => getIndustrialCostPageViewFromUrl() ?? defaultView,
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<NavGroupKey, boolean>>({
    workflows: false,
    'indirect-costs': false,
    'direct-production': false,
    plant: false,
    inventory: false,
    setup: false,
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [desktopSidebarContentVisible, setDesktopSidebarContentVisible] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const isDesktopSidebarExpanded =
    !shouldUseCompactSidebar &&
    (desktopSidebarOpen || Boolean(userMenuAnchorEl));

  const isDesktopSidebarContentVisible =
    shouldUseCompactSidebar || desktopSidebarContentVisible;

  useEffect(() => {
    if (shouldUseCompactSidebar) {
      setDesktopSidebarContentVisible(false);
      return;
    }

    if (!isDesktopSidebarExpanded) {
      setDesktopSidebarContentVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDesktopSidebarContentVisible(true);
    }, 90);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isDesktopSidebarExpanded, shouldUseCompactSidebar]);

  useEffect(() => {
    if (
      industrialContextQuery.isLoading ||
      industrialWorkflowsContextQuery.isLoading
    ) {
      return;
    }

    const requestedView =
      getIndustrialCostPageViewFromUrl();

    const nextView =
      requestedView &&
      accessibleViews.has(requestedView)
        ? requestedView
        : defaultView;

    if (nextView !== view) {
      setView(nextView);
    }

    replaceIndustrialCostPageViewInUrl(nextView);
  }, [
    accessibleViews,
    defaultView,
    industrialContextQuery.isLoading,
    industrialWorkflowsContextQuery.isLoading,
    view,
  ]);

  const setCurrentView = (nextView: IndustrialCostPageViewKey) => {
    if (!accessibleViews.has(nextView)) {
      return;
    }

    setView(nextView);
    replaceIndustrialCostPageViewInUrl(nextView);
    setMobileSidebarOpen(false);

    if (isViewInGroup(nextView, 'workflows')) {
      setExpandedGroups((prev) => ({ ...prev, workflows: true }));
    }
    if (isViewInGroup(nextView, 'indirect-costs')) {
      setExpandedGroups((prev) => ({ ...prev, 'indirect-costs': true }));
    }
    if (isViewInGroup(nextView, 'direct-production')) {
      setExpandedGroups((prev) => ({ ...prev, 'direct-production': true }));
    }
    if (isViewInGroup(nextView, 'plant')) {
      setExpandedGroups((prev) => ({ ...prev, plant: true }));
    }
    if (isViewInGroup(nextView, 'inventory')) {
      setExpandedGroups((prev) => ({ ...prev, inventory: true }));
    }
    if (isViewInGroup(nextView, 'setup')) {
      setExpandedGroups((prev) => ({ ...prev, setup: true }));
    }
  };

  const toggleGroup = (group: NavGroupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchorEl(null);
  };

  const handleRequestLogout = () => {
    handleCloseUserMenu();
    setLogoutDialogOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleConfirmLogout = async () => {
    try {
      setLogoutDialogOpen(false);
      await signOut();
      window.location.href = '/industrial-cost';
    } catch (error) {
      console.error('Industrial Cost logout error', error);
    }
  };

  const content = useMemo(() => {
    if (
      !canViewCosts &&
      [
        'movements',
        'classification',
        'primary-allocations',
        'secondary-allocations',
        'final-allocation-rules',
        'production',
        'direct-inputs',
        'labor',
        'transformations',
        'technical-consumptions',
        'direct-material-valuations',
        'applied-direct-costs',
        'products',
        'stages',
        'direct-materials',
        'direct-cost-categories',
        ...(canManageImports ? [] : ['import-center']),
      ].includes(view)
    ) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Costes no disponibles"
          description="Tu usuario no tiene permisos para consultar información económica o de costes del sistema industrial."
        />
      );
    }

    if (view === 'workflows-cases' && !canViewWorkflowsCases) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Expedientes no disponibles"
          description="Tu usuario no tiene permisos para consultar expedientes."
        />
      );
    }

    if (view === 'workflows-config' && !canManageWorkflowsConfig) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Procesos no disponibles"
          description="Tu usuario no tiene permisos para consultar o configurar procesos de trabajo."
        />
      );
    }

    if (
      !canManageInventory &&
      [
        'inventory-register',
        'material-control',
        'warehouse-layout',
        'recount-sheet',
      ].includes(view)
    ) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Inventario no disponible"
          description="Tu usuario no tiene permisos para gestionar el inventario industrial."
        />
      );
    }

    if (view === 'plant-dashboard' && !canManageInventory) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Panel de planta no disponible"
          description="Tu usuario no dispone del permiso de inventario necesario."
        />
      );
    }

    if (view === 'production-reports-operator' && !canUseProductionReportsOperator) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Puesto operario no disponible"
          description="Tu usuario no tiene permisos para consultar u operar partes de producción."
        />
      );
    }

    if (view === 'production-reports-control' && !canViewFactoryControl) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Control de fábrica no disponible"
          description="Tu usuario no tiene permisos para consultar el estado, histórico o rendimiento de fábrica."
        />
      );
    }

    if (view === 'production-planning' && !canManageProductionPlanning) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Planificador no disponible"
          description="Tu usuario no tiene permisos para consultar la planificación de fábrica."
        />
      );
    }

    if (view === 'production-reports-config' && !canManagePlantConfig) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Configuración no disponible"
          description="Tu usuario no tiene permisos para modificar la configuración de planta."
        />
      );
    }

    if (view === 'plant-users' && !canManagePlantUsers) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Usuarios no disponible"
          description="Tu usuario no tiene permisos para gestionar usuarios de planta."
        />
      );
    }

    if (view === 'results' && !canViewResults) {
      return (
        <IndustrialCostSectionPlaceholder
          title="Resultados no disponibles"
          description="Tu usuario no tiene permisos para consultar los resultados del sistema industrial."
        />
      );
    }

    if (view === 'workflows-cases') return <IndustrialWorkflowsCasesView />;
    if (view === 'workflows-config') return <IndustrialWorkflowsConfigView />;

    if (view === 'guide') return <IndustrialCostGuideView />;
    if (view === 'stages') return <IndustrialCostStagesView />;
    if (view === 'products') return <IndustrialCostProductsView />;
    if (view === 'direct-materials') return <IndustrialCostDirectMaterialsView />;
    if (view === 'movements') return <IndustrialCostMovementsView />;
    if (view === 'classification') return <IndustrialCostClassificationView />;

    if (view === 'applied-direct-costs') return <IndustrialCostDirectCostsView />;

    if (view === 'direct-inputs') return <IndustrialCostDirectInputsView />;

    if (view === 'labor') return <IndustrialCostLaborView />;

    if (view === 'transformations') return <IndustrialCostTransformationsView />;

    if (view === 'technical-consumptions') return <IndustrialCostTechnicalConsumptionsView />;

    if (view === 'direct-material-valuations') {
      return <IndustrialCostDirectMaterialValuationsView />;
    }

    if (view === 'direct-cost-categories') {
      return (
        <IndustrialCostSectionPlaceholder
          title="Categorías directas"
          description="La configuración estructural de categorías directas se moverá a Setup para separar parametrización de operación."
        />
      );
    }

    if (view === 'import-center') {
      return (
        <IndustrialCostDataSourcesView
          onNavigate={(nextView) =>
            setCurrentView(nextView)
          }
        />
      );
    }
    if (view === 'plant-users') return <IndustrialCostPlantUsersView />;

    if (view === 'primary-allocations') return <IndustrialCostPrimaryAllocationsView />;
    if (view === 'secondary-allocations') return <IndustrialCostSecondaryAllocationsView />;
    if (view === 'production') return <IndustrialCostProductionView />;
    if (view === 'final-allocation-rules') return <IndustrialCostFinalAllocationRulesView />;
    if (view === 'inventory-register') return <IndustrialCostInventoryRegisterView />;
    if (view === 'material-control') return <IndustrialCostMaterialControlView />;
    if (view === 'supplier-matches') return <IndustrialCostSupplierMatchesView />;
    if (view === 'recount-sheet') {
      return (
        <IndustrialCostRecountSheetView
          canManageImports={canManageImports}
          onOpenSuppliers={() => setCurrentView('supplier-matches')}
        />
      );
    }
    if (view === 'plant-dashboard') {
      return (
        <IndustrialCostPlantDashboardView
          onOpenFileReview={
            // IND-007: solo si puede abrir Gestión de datos.
            canManageImports || canViewCosts
              ? () => {
                  // IND-004: abre Gestión de datos en «Revisa tus ficheros».
                  window.location.hash = 'revisa-tus-ficheros';
                  setCurrentView('import-center');
                }
              : undefined
          }
        />
      );
    }
    if (view === 'warehouse-layout') return <IndustrialCostWarehouseLayoutView />;
    if (view === 'production-reports-operator') {
      return <IndustrialCostProductionReportsOperatorView permissions={permissions} />;
    }
    if (view === 'production-reports-control') {
      return <IndustrialCostProductionReportsControlView permissions={controlViewPermissions} />;
    }
    if (view === 'production-planning') {
      return <IndustrialCostProductionPlanningView />;
    }
    if (view === 'production-reports-config') {
      return <IndustrialCostProductionReportsConfigView />;
    }
    if (view === 'results') return <IndustrialCostResultsView />;
    return <IndustrialCostSummaryView />;
  }, [
    view,
    canUseProductionReportsOperator,
    canViewWorkflowsCases,
    canManageWorkflowsConfig,
    canViewFactoryControl,
    canManagePlantConfig,
    canManagePlantUsers,
    canManageProductionPlanning,
    canManageInventory,
    canManageImports,
    canViewCosts,
    canViewResults,
    controlViewPermissions,
    permissions,
  ]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#081120',
        color: '#f8fafc',
        pl: shouldUseCompactSidebar ? { xs: 2, md: 3, xl: 4 } : 0,
        pr: { xs: 2, md: 3, xl: 4 },
        py: { xs: 2.5, md: 3 },
      }}
    >
      <RevealSection>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: shouldUseCompactSidebar
              ? '1fr'
              : '72px minmax(0, 1fr)',
            gap: shouldUseCompactSidebar ? 3 : 2.5,
            alignItems: 'start',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<MenuRounded />}
            onClick={() => setMobileSidebarOpen(true)}
            sx={{
              display: shouldUseCompactSidebar
                ? 'inline-flex'
                : 'none',
              justifySelf: 'start',
              color: '#bae6fd',
              borderColor: alpha('#38bdf8', 0.28),
              fontWeight: 900,
              textTransform: 'none',
            }}
          >
            Menú
          </Button>

          <Box
            onClick={() => setMobileSidebarOpen(false)}
            sx={{
              display:
                shouldUseCompactSidebar &&
                mobileSidebarOpen
                  ? 'block'
                  : 'none',
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              bgcolor: 'rgba(2,6,23,0.68)',
              backdropFilter: 'blur(3px)',
            }}
          />

          <Box
            onMouseEnter={() => {
              if (!shouldUseCompactSidebar) {
                setDesktopSidebarOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (!shouldUseCompactSidebar && !userMenuAnchorEl) {
                setDesktopSidebarOpen(false);
              }
            }}
            sx={{
              border: `1px solid ${alpha('#94a3b8', 0.16)}`,
              bgcolor: 'rgba(8,18,36,0.97)',
              backdropFilter: 'blur(12px)',
              borderRadius: shouldUseCompactSidebar
                ? 0
                : 4,
              p: shouldUseCompactSidebar
                ? 1.5
                : isDesktopSidebarExpanded
                  ? 1.5
                  : 1,
              minHeight: shouldUseCompactSidebar
                ? '100vh'
                : 'calc(100vh - 48px)',
              display: shouldUseCompactSidebar
                ? mobileSidebarOpen
                  ? 'flex'
                  : 'none'
                : 'flex',
              flexDirection: 'column',
              position: shouldUseCompactSidebar
                ? 'fixed'
                : 'sticky',
              top: shouldUseCompactSidebar ? 0 : 24,
              left: shouldUseCompactSidebar
                ? 0
                : 'auto',
              bottom: shouldUseCompactSidebar
                ? 0
                : 'auto',
              width: shouldUseCompactSidebar
                ? 'min(88vw, 340px)'
                : isDesktopSidebarExpanded
                  ? 300
                  : 72,
              maxHeight: shouldUseCompactSidebar
                ? '100vh'
                : 'none',
              overflowX: shouldUseCompactSidebar ? 'visible' : 'hidden',
              overflowY: shouldUseCompactSidebar
                ? 'auto'
                : 'visible',
              transition: shouldUseCompactSidebar
                ? undefined
                : 'width 0.18s ease, padding 0.18s ease, box-shadow 0.16s ease',
              boxShadow:
                !shouldUseCompactSidebar && isDesktopSidebarExpanded
                  ? '0 24px 60px rgba(2,6,23,0.46)'
                  : 'none',
              zIndex: shouldUseCompactSidebar
                ? 1301
                : 1200,
            }}
          >
            <Box
              sx={{
                display: !shouldUseCompactSidebar && !isDesktopSidebarExpanded
                  ? 'flex'
                  : 'none',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 'calc(100vh - 80px)',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  pt: 0.75,
                  pb: 2,
                  borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="PROAIndustrial"
                  sx={{
                    height: 30,
                    width: 'auto',
                    objectFit: 'contain',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))',
                  }}
                />
              </Box>

              <Box
                sx={{
                  pt: 2,
                  flex: 1,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 1,
                    pb: 1.25,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  Navegación
                </Typography>

                <List
                  disablePadding
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {canViewCosts ? (
                    <>
                      <DesktopNavRailButton
                        label="Guía"
                        icon={<MenuBookRounded />}
                        selected={view === 'guide'}
                        onClick={() => setCurrentView('guide')}
                      />
                      <DesktopNavRailButton
                        label="Resumen"
                        icon={<DashboardRounded />}
                        selected={view === 'summary'}
                        onClick={() => setCurrentView('summary')}
                      />
                    </>
                  ) : null}

                  {canViewCosts ? (
                    <>
                      <DesktopNavRailButton
                        label="Costes indirectos"
                        icon={<PaymentsRounded />}
                        selected={isViewInGroup(view, 'indirect-costs')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            'indirect-costs': true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={5}
                        visible={false}
                      />
                    </>
                  ) : null}

                  {canViewCosts || canViewResults ? (
                    <>
                      <DesktopNavRailButton
                        label="Producción y directos"
                        icon={<PrecisionManufacturingRounded />}
                        selected={isViewInGroup(view, 'direct-production')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            'direct-production': true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={(canViewCosts ? 7 : 0) + (canViewResults ? 1 : 0)}
                        visible={false}
                      />
                    </>
                  ) : null}

                  {canAccessWorkflows ? (
                    <>
                      <DesktopNavRailButton
                        label="Organizador"
                        icon={<AccountTreeRounded />}
                        selected={isViewInGroup(view, 'workflows')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            workflows: true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={
                          (canViewWorkflowsCases ? 1 : 0) +
                          (canManageWorkflowsConfig ? 1 : 0)
                        }
                        visible={false}
                      />
                    </>
                  ) : null}

                  {canViewPlant ? (
                    <>
                      <DesktopNavRailButton
                        label="Planta"
                        icon={<FactoryRounded />}
                        selected={isViewInGroup(view, 'plant')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            plant: true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={
                          (canUseProductionReportsOperator ? 1 : 0) +
                          (canViewFactoryControl ? 1 : 0) +
                          (canManageProductionPlanning ? 1 : 0) +
                          (canManagePlantConfig ? 1 : 0)
                        }
                        visible={false}
                      />
                    </>
                  ) : null}

                  {canManageInventory || canManageImports ? (
                    <>
                      <DesktopNavRailButton
                        label="Inventario"
                        icon={<Inventory2Rounded />}
                        selected={isViewInGroup(view, 'inventory')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            inventory: true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={3}
                        visible={false}
                      />
                    </>
                  ) : null}

                  {(canViewCosts || canManagePlantUsers || canAccessPlatformAdmin) ? (
                    <>
                      <DesktopNavRailButton
                        label="Setup"
                        icon={<SettingsRounded />}
                        selected={isViewInGroup(view, 'setup')}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            setup: true,
                          }))
                        }
                      />
                      <DesktopNavRailGroupSpacer
                        count={
                          (canViewCosts ? 5 : 0) +
                          (canManagePlantUsers ? 1 : 0) +
                          (canAccessPlatformAdmin ? 1 : 0)
                        }
                        visible={false}
                      />
                    </>
                  ) : null}
                </List>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <IconButton
                  title={activeUserName}
                  aria-label="Usuario"
                  onClick={handleOpenUserMenu}
                  sx={{
                    width: 46,
                    height: 46,
                    p: 0,
                    borderRadius: 2.5,
                    bgcolor: alpha('#ffffff', 0.025),
                    border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.05),
                      borderColor: alpha('#38bdf8', 0.22),
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: alpha('#38bdf8', 0.1),
                      color: '#38bdf8',
                      border: `1px solid ${alpha('#38bdf8', 0.3)}`,
                    }}
                  >
                    <AccountCircle fontSize="medium" />
                  </Avatar>
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
                transition: shouldUseCompactSidebar
                  ? undefined
                  : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                px: 1.25,
                pt: 0.5,
                pb: 2.25,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="PROAIndustrial"
                    sx={{
                      height: 30,
                      width: 'auto',
                      objectFit: 'contain',
                      flexShrink: 0,
                      filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))',
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      letterSpacing: '-0.6px',
                      fontSize: '1.18rem',
                      lineHeight: 1,
                    }}
                  >
                    PROAIndustrial
                  </Typography>

                  <IconButton
                    aria-label="Cerrar menú"
                    onClick={() => setMobileSidebarOpen(false)}
                    sx={{
                      display: shouldUseCompactSidebar
                        ? 'inline-flex'
                        : 'none',
                      ml: 'auto',
                      color: '#94a3b8',
                    }}
                  >
                    <CloseRounded />
                  </IconButton>
                </Stack>

                <Typography
                  sx={{
                    pl: 5.25,
                    color: '#64748b',
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    fontSize: '0.68rem',
                    lineHeight: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Industrial System
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
                transition: shouldUseCompactSidebar
                  ? undefined
                  : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                pt: 2,
                flex: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1,
                  pb: 1.25,
                  color: '#64748b',
                  fontWeight: 800,
                  letterSpacing: 1.1,
                  textTransform: 'uppercase',
                }}
              >
                Navegación
              </Typography>

              <List disablePadding>
                {canViewCosts ? (
                  <>
                    <NavItem
                      label="Guía"
                      selected={view === 'guide'}
                      onClick={() => setCurrentView('guide')}
                    />
                    <NavItem
                      label="Resumen"
                      selected={view === 'summary'}
                      onClick={() => setCurrentView('summary')}
                    />
                  </>
                ) : null}

                {canViewCosts ? (
                  <NavGroup
                    title="Costes indirectos"
                    expanded={expandedGroups['indirect-costs']}
                    active={isViewInGroup(view, 'indirect-costs')}
                    onToggle={() => toggleGroup('indirect-costs')}
                  >
                    <NavItem
                      label="Movimientos"
                      nested
                      selected={view === 'movements'}
                      onClick={() => setCurrentView('movements')}
                    />
                    <NavItem
                      label="Clasificación"
                      nested
                      selected={view === 'classification'}
                      onClick={() => setCurrentView('classification')}
                    />
                    <NavItem
                      label="Reparto primario"
                      nested
                      selected={view === 'primary-allocations'}
                      onClick={() => setCurrentView('primary-allocations')}
                    />
                    <NavItem
                      label="Reparto secundario"
                      nested
                      selected={view === 'secondary-allocations'}
                      onClick={() => setCurrentView('secondary-allocations')}
                    />
                    <NavItem
                      label="Reglas finales"
                      nested
                      selected={view === 'final-allocation-rules'}
                      onClick={() => setCurrentView('final-allocation-rules')}
                    />
                  </NavGroup>
                ) : null}

                {canViewCosts || canViewResults ? (
                  <NavGroup
                    title="Producción y directos"
                    expanded={expandedGroups['direct-production']}
                    active={isViewInGroup(view, 'direct-production')}
                    onToggle={() => toggleGroup('direct-production')}
                  >
                    {canViewCosts ? (
                      <>
                        <NavItem
                          label="Producción"
                          nested
                          selected={view === 'production'}
                          onClick={() => setCurrentView('production')}
                        />
                        <NavItem
                          label="Entradas"
                          nested
                          selected={view === 'direct-inputs'}
                          onClick={() => setCurrentView('direct-inputs')}
                        />
                        <NavItem
                          label="Mano de obra"
                          nested
                          selected={view === 'labor'}
                          onClick={() => setCurrentView('labor')}
                        />
                        <NavItem
                          label="Transformación"
                          nested
                          selected={view === 'transformations'}
                          onClick={() => setCurrentView('transformations')}
                        />
                        <NavItem
                          label="Consumos técnicos"
                          nested
                          selected={view === 'technical-consumptions'}
                          onClick={() => setCurrentView('technical-consumptions')}
                        />
                        <NavItem
                          label="Valoración materiales"
                          nested
                          selected={view === 'direct-material-valuations'}
                          onClick={() => setCurrentView('direct-material-valuations')}
                        />
                        <NavItem
                          label="Directos aplicados"
                          nested
                          selected={view === 'applied-direct-costs'}
                          onClick={() => setCurrentView('applied-direct-costs')}
                        />
                      </>
                    ) : null}

                    {canViewResults ? (
                      <NavItem
                        label="Resultados"
                        nested
                        selected={view === 'results'}
                        onClick={() => setCurrentView('results')}
                      />
                    ) : null}
                  </NavGroup>
                ) : null}

                {canAccessWorkflows ? (
                  <NavGroup
                    title="Organizador"
                    expanded={expandedGroups.workflows}
                    active={isViewInGroup(view, 'workflows')}
                    onToggle={() => toggleGroup('workflows')}
                  >
                    {canViewWorkflowsCases ? (
                      <NavItem
                        label="Expedientes"
                        nested
                        selected={view === 'workflows-cases'}
                        onClick={() => setCurrentView('workflows-cases')}
                      />
                    ) : null}

                    {canManageWorkflowsConfig ? (
                      <NavItem
                        label="Flujos y etapas"
                        nested
                        selected={view === 'workflows-config'}
                        onClick={() => setCurrentView('workflows-config')}
                      />
                    ) : null}
                  </NavGroup>
                ) : null}

                {canViewPlant ? (
                  <NavGroup
                    title="Planta"
                    expanded={expandedGroups.plant}
                    active={isViewInGroup(view, 'plant')}
                    onToggle={() => toggleGroup('plant')}
                  >
                    {canManageInventory ? (
                      <NavItem
                        label="Panel de planta"
                        nested
                        selected={view === 'plant-dashboard'}
                        onClick={() => setCurrentView('plant-dashboard')}
                      />
                    ) : null}

                    {/* IND-007: sin acceso a Costes, Gestión de datos va en Planta. */}
                    {canManageImports && !canViewCosts ? (
                      <NavItem
                        label="Gestión de datos"
                        nested
                        selected={view === 'import-center'}
                        onClick={() => setCurrentView('import-center')}
                      />
                    ) : null}

                    {canUseProductionReportsOperator ? (
                      <NavItem
                        label="Puesto operario"
                        nested
                        selected={view === 'production-reports-operator'}
                        onClick={() => setCurrentView('production-reports-operator')}
                      />
                    ) : null}
                    {canViewFactoryControl ? (
                      <NavItem
                        label="Control de fábrica"
                        nested
                        selected={view === 'production-reports-control'}
                        onClick={() => setCurrentView('production-reports-control')}
                      />
                    ) : null}
                    {canManageProductionPlanning ? (
                      <NavItem
                        label="Planificador"
                        nested
                        selected={view === 'production-planning'}
                        onClick={() => setCurrentView('production-planning')}
                      />
                    ) : null}
                    {canManagePlantConfig ? (
                      <NavItem
                        label="Configuración"
                        nested
                        selected={view === 'production-reports-config'}
                        onClick={() => setCurrentView('production-reports-config')}
                      />
                    ) : null}
                  </NavGroup>
                ) : null}

                {canManageInventory || canManageImports ? (
                  <NavGroup
                    title="Inventario"
                    expanded={expandedGroups.inventory}
                    active={isViewInGroup(view, 'inventory')}
                    onToggle={() => toggleGroup('inventory')}
                  >
                    {canManageInventory ? (
                      <>
                        <NavItem
                          label="Registro"
                          nested
                          selected={view === 'inventory-register'}
                          onClick={() => setCurrentView('inventory-register')}
                        />
                        <NavItem
                          label="Control de materiales"
                          nested
                          selected={view === 'material-control'}
                          onClick={() => setCurrentView('material-control')}
                        />

                        <NavItem
                          label="Espacios"
                          nested
                          selected={view === 'warehouse-layout'}
                          onClick={() => setCurrentView('warehouse-layout')}
                        />
                        <NavItem
                          label="Hoja de recuento (en pruebas)"
                          nested
                          selected={view === 'recount-sheet'}
                          onClick={() => setCurrentView('recount-sheet')}
                        />
                      </>
                    ) : null}
                    {canManageImports ? (
                      <NavItem
                        label="Proveedores del recuento"
                        nested
                        selected={view === 'supplier-matches'}
                        onClick={() => setCurrentView('supplier-matches')}
                      />
                    ) : null}
                  </NavGroup>
                ) : null}

                {(canViewCosts || canManagePlantUsers || canAccessPlatformAdmin) ? (
                  <NavGroup
                    title="Setup"
                    expanded={expandedGroups.setup}
                    active={isViewInGroup(view, 'setup')}
                    onToggle={() => toggleGroup('setup')}
                  >
                    {canViewCosts ? (
                      <>
                        <NavItem
                          label="Productos"
                          nested
                          selected={view === 'products'}
                          onClick={() => setCurrentView('products')}
                        />
                        <NavItem
                          label="Etapas"
                          nested
                          selected={view === 'stages'}
                          onClick={() => setCurrentView('stages')}
                        />
                        <NavItem
                          label="Materiales directos"
                          nested
                          selected={view === 'direct-materials'}
                          onClick={() => setCurrentView('direct-materials')}
                        />
                        <NavItem
                          label="Categorías directas"
                          nested
                          selected={view === 'direct-cost-categories'}
                          onClick={() => setCurrentView('direct-cost-categories')}
                        />
                        <NavItem
                          label="Gestión de datos"
                          nested
                          selected={view === 'import-center'}
                          onClick={() => setCurrentView('import-center')}
                        />
                      </>
                    ) : null}
                    {canManagePlantUsers ? (
                      <NavItem
                        label="Usuarios"
                        nested
                        selected={view === 'plant-users'}
                        onClick={() => setCurrentView('plant-users')}
                      />
                    ) : null}

                    {canAccessPlatformAdmin ? (
                      <NavItem
                        label="Administrador PROA"
                        nested
                        selected={false}
                        onClick={() => {
                          setMobileSidebarOpen(false);
                          window.location.href = '/platform-admin';
                        }}
                      />
                    ) : null}
                  </NavGroup>
                ) : null}
              </List>
            </Box>

            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
                transition: shouldUseCompactSidebar
                  ? undefined
                  : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                mt: 2,
                pt: 1.5,
                borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <ListItemButton
                onClick={handleOpenUserMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 1.25,
                  borderRadius: 2.5,
                  bgcolor: alpha('#ffffff', 0.025),
                  border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.05),
                    border: `1px solid ${alpha('#38bdf8', 0.22)}`,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: alpha('#38bdf8', 0.1),
                    color: '#38bdf8',
                    border: `1px solid ${alpha('#38bdf8', 0.3)}`,
                  }}
                >
                  <AccountCircle fontSize="medium" />
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 850,
                      fontSize: '0.9rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {activeUserName}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      color: '#64748b',
                      fontWeight: 700,
                      fontSize: '0.76rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {activeUserSubtitle}
                  </Typography>
                </Box>
              </ListItemButton>

              <Menu
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleCloseUserMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{
                  zIndex: shouldUseCompactSidebar ? 1402 : 1300,
                }}
              >
                <MenuItem onClick={handleRequestLogout}>Cerrar sesión</MenuItem>
              </Menu>

              <Dialog open={logoutDialogOpen} onClose={handleCancelLogout}>
                <DialogTitle>Cerrar sesión</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    ¿Seguro que quieres cerrar la sesión de PROA Industrial?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelLogout}>Cancelar</Button>
                  <Button color="error" variant="contained" onClick={handleConfirmLogout}>
                    Cerrar sesión
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <RevealSection delay={0.04}>{content}</RevealSection>
          </Box>
        </Box>
      </RevealSection>
    </Box>
  );
}